import { useEffect, useRef, useState } from "react";
import TomSelect from "tom-select";
import Swal from "sweetalert2";

const LemburForm = ({ isModalOpen, onClose }) => {
    const [karyawanOptions, setKaryawanOptions] = useState([]);
    const [tanggalLembur, setTanggalLembur] = useState("");
    const [jamPulangJadwal, setJamPulangJadwal] = useState("");
    const [realisasiPulang, setRealisasiPulang] = useState("");
    const [alasanLembur, setAlasanLembur] = useState("");
    const [karyawanId, setKaryawanId] = useState("");

    // Canvas TTD Petugas Lembur
    const canvasRefPetugas = useRef(null);
    const isDrawingPetugas = useRef(false);
    const ctxRefPetugas = useRef(null);

    // Canvas TTD Koordinator
    const canvasRefKoordinator = useRef(null);
    const isDrawingKoordinator = useRef(false);
    const ctxRefKoordinator = useRef(null);

    // TomSelect
    const tomSelectRef = useRef(null);
    const tomInstance = useRef(null);

    const styles = {
        field: { marginBottom: 16 },
        label: { display: "block", marginBottom: 8, fontWeight: 500 },
        input: {
            width: "100%",
            padding: "12px",
            borderRadius: 12,
            border: "1.5px solid #ddd",
            fontSize: 15,
            backgroundColor: "#fff",
            outline: "none",
            boxSizing: "border-box",
        },
    };

    // ── Helper: Fetch + filter karyawan berdasarkan team_absen == id login ──
    const fetchAndFilterKaryawan = async () => {
        try {
            const myData = JSON.parse(localStorage.getItem("data_karyawan"));
            const myKaryawanId = myData?.id;
            const myUserId = myData?.user_id;
            if (!myKaryawanId && !myUserId) return [];

            // 1. Coba dari localStorage dulu (data dari riwayat-absen yang punya team_absen)
            const rawList = localStorage.getItem("karyawan_pengganti_list");
            if (rawList) {
                const parsed = JSON.parse(rawList);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    // Cek apakah data punya field team_absen
                    const hasTeamAbsen = parsed.some(k => k.team_absen !== undefined);
                    if (hasTeamAbsen) {
                        const filtered = parsed.filter(
                            (k) => (myUserId && String(k.team_absen) === String(myUserId)) ||
                                   (myKaryawanId && String(k.team_absen) === String(myKaryawanId))
                        );
                        console.log(`[LemburForm] Filter team_absen (user_id=${myUserId}, id=${myKaryawanId}): ${filtered.length} karyawan`);
                        return filtered;
                    }
                }
            }

            // 2. Fallback: fetch dari API riwayat-absen yang menyertakan team_absen
            const fetchId = myKaryawanId || myUserId;
            console.log(`[LemburForm] Fetching karyawan dari API untuk id=${fetchId}`);
            const res = await fetch(`https://jne-cilacap.com/slip/public/api/riwayat-absen/${fetchId}`);
            const json = await res.json();
            if (json.success && Array.isArray(json.karyawan_pengganti_list)) {
                // Simpan ke localStorage untuk digunakan berikutnya
                try { localStorage.setItem("karyawan_pengganti_list", JSON.stringify(json.karyawan_pengganti_list)); } catch (e) {}
                const filtered = json.karyawan_pengganti_list.filter(
                    (k) => (myUserId && String(k.team_absen) === String(myUserId)) ||
                           (myKaryawanId && String(k.team_absen) === String(myKaryawanId))
                );
                console.log(`[LemburForm] API result: ${filtered.length} karyawan dalam tim`);
                return filtered;
            }
            return [];
        } catch (err) {
            console.log("Gagal fetch karyawan:", err);
            return [];
        }
    };

    const getPeriod = () => {
        const now = new Date();
        let month = now.getMonth(), year = now.getFullYear();
        if (now.getDate() >= 21) month++;
        if (month > 11) { month = 0; year++; }
        return `${new Date(year, month).toLocaleString("en-US", { month: "long" })} ${year}`;
    };

    // ── Hitung Durasi Lembur ──
    const hitungDurasi = () => {
        if (!jamPulangJadwal || !realisasiPulang) return 0;

        const [startH, startM] = jamPulangJadwal.split(":").map(Number);
        const [endH, endM] = realisasiPulang.split(":").map(Number);

        let startValue = startH + startM / 60;
        let endValue = endH + endM / 60;

        // Jika pulang besoknya (e.g. jadwal 23:00, realisasi 01:00)
        if (endValue < startValue) {
            endValue += 24;
        }

        const durasi = endValue - startValue;
        return parseFloat(durasi.toFixed(2));
    };

    const durasiJam = hitungDurasi();

    // ── Hitung Estimasi Uang Lembur ──
    // 1:00 - 1:29 = Rp10.000
    // 1:30 - 1:59 = Rp15.000
    // 2:00 - 2:29 = Rp20.000
    // 2:30 - 2:59 = Rp25.000
    // ...
    // 5:30 atau lebih = Rp55.000
    const hitungEstimasiUang = (durasi) => {
        if (durasi < 1) return 0;

        // Ambil total menit
        const totalMenit = Math.floor(durasi * 60);

        // Setiap 30 menit setelah 1 jam menambah Rp5.000
        const slotTambahan = Math.floor((totalMenit - 60) / 30);

        const nominal = 10000 + (slotTambahan * 5000);

        return Math.min(nominal, 55000);
    };

    const estimasiUang = hitungEstimasiUang(durasiJam);

    const formatDurasi = (durasiDesimal) => {
        if (!durasiDesimal) return "0 jam";
        const jam = Math.floor(durasiDesimal);
        const menit = Math.round((durasiDesimal - jam) * 60);
        let hasil = [];
        if (jam > 0) hasil.push(`${jam} jam`);
        if (menit > 0) hasil.push(`${menit} menit`);
        return hasil.join(" ") || "0 jam";
    };

    // ── Setup canvas saat modal dibuka ──
    useEffect(() => {
        if (!isModalOpen) return;

        const setupCanvas = (ref, ctxRef) => {
            if (!ref.current) return;
            const canvas = ref.current;
            const ctx = canvas.getContext("2d");
            ctx.lineCap = "round";
            ctx.strokeStyle = "#333";
            ctx.lineWidth = 3;
            ctxRef.current = ctx;
            const resize = () => {
                const { width } = canvas.getBoundingClientRect();
                canvas.width = width * window.devicePixelRatio;
                canvas.height = 150 * window.devicePixelRatio;
                ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
            };
            resize();
            return resize;
        };

        const resizePetugas = setupCanvas(canvasRefPetugas, ctxRefPetugas);
        const resizeKoordinator = setupCanvas(canvasRefKoordinator, ctxRefKoordinator);

        const handleResize = () => {
            resizePetugas && resizePetugas();
            resizeKoordinator && resizeKoordinator();
        };

        window.addEventListener("resize", handleResize);

        // Setup TomSelect (inisialisasi dulu kosong, isi setelah fetch selesai)
        if (!tomInstance.current && tomSelectRef.current) {
            tomInstance.current = new TomSelect(tomSelectRef.current, {
                valueField: "id",
                labelField: "nama",
                searchField: "nama",
                placeholder: "Memuat karyawan...",
                options: [],
                onChange: (val) => setKaryawanId(val),
                render: {
                    option: (data, escape) => `
                        <div style="padding:6px 8px">
                            <strong>${escape(data.nama)}</strong>
                        </div>
                    `,
                    item: (data, escape) => `
                        <div>${escape(data.nama)}</div>
                    `,
                },
            });

            // Fetch + filter karyawan async, lalu isi TomSelect
            fetchAndFilterKaryawan().then((dataKaryawan) => {
                // Tambahkan karyawan yang login ke daftar jika belum ada
                let finalList = [...dataKaryawan];
                try {
                    const myData = JSON.parse(localStorage.getItem("data_karyawan"));
                    const selfId = myData?.id || myData?.user_id;
                    if (selfId) {
                        const sudahAda = finalList.some(k => String(k.id) === String(selfId));
                        if (!sudahAda) {
                            const namaLengkap = `${myData.nama_depan || ""} ${myData.nama_belakang || ""}`.trim() || myData.nama || "Saya";
                            // Sisipkan sebagai opsi pertama
                            finalList = [
                                { id: String(selfId), nama: namaLengkap },
                                ...finalList,
                            ];
                        }
                    }
                } catch (e) { }

                setKaryawanOptions(finalList);
                if (!tomInstance.current) return;
                tomInstance.current.clearOptions();
                tomInstance.current.addOptions(finalList);
                tomInstance.current.settings.placeholder = finalList.length === 0
                    ? "Tidak ada karyawan dalam tim"
                    : "Pilih karyawan lembur";
                tomInstance.current.inputState();

                // Auto-select karyawan login
                try {
                    const myData = JSON.parse(localStorage.getItem("data_karyawan"));
                    const selfId = myData?.id || myData?.user_id;
                    if (selfId) tomInstance.current.setValue(String(selfId));
                } catch (e) { }
            });
        }

        return () => {
            window.removeEventListener("resize", handleResize);
            if (tomInstance.current) {
                tomInstance.current.destroy();
                tomInstance.current = null;
            }
        };
    }, [isModalOpen]);

    // Reset isi form
    useEffect(() => {
        if (isModalOpen && canvasRefPetugas.current && canvasRefKoordinator.current) {
            canvasRefPetugas.current.getContext("2d")?.clearRect(0, 0, canvasRefPetugas.current.width, canvasRefPetugas.current.height);
            canvasRefKoordinator.current.getContext("2d")?.clearRect(0, 0, canvasRefKoordinator.current.width, canvasRefKoordinator.current.height);
        }
        if (!isModalOpen) {
            setTanggalLembur("");
            setJamPulangJadwal("");
            setRealisasiPulang("");
            setAlasanLembur("");
            setKaryawanId("");
            if (tomInstance.current) tomInstance.current.clear();
        }
    }, [isModalOpen]);

    // ── Drawing handlers ──
    const getPos = (ref, e, isTouch) => {
        const rect = ref.current.getBoundingClientRect();
        return {
            x: (isTouch ? e.touches[0].clientX : e.clientX) - rect.left,
            y: (isTouch ? e.touches[0].clientY : e.clientY) - rect.top,
        };
    };

    const handleDrawStart = (target, ref, ctxRef, e, isTouch = false) => {
        target.current = true;
        const { x, y } = getPos(ref, e, isTouch);
        ctxRef.current.beginPath();
        ctxRef.current.moveTo(x, y);
    };

    const handleDrawMove = (target, ref, ctxRef, e, isTouch = false) => {
        if (!target.current) return;
        const { x, y } = getPos(ref, e, isTouch);
        ctxRef.current.lineTo(x, y);
        ctxRef.current.stroke();
    };

    const handleDrawEnd = (target) => { target.current = false; };

    const clearSignature = (ref) => {
        if (!ref.current) return;
        const ctx = ref.current.getContext("2d");
        ctx?.clearRect(0, 0, ref.current.width, ref.current.height);
    };

    // ── Submit ──
    const handleSubmit = async () => {
        if (!karyawanId) {
            await Swal.fire({ icon: "warning", title: "Karyawan Kosong", text: "Pilih karyawan lembur." });
            return;
        }
        if (!tanggalLembur || !jamPulangJadwal || !realisasiPulang || !alasanLembur) {
            await Swal.fire({ icon: "warning", title: "Form Belum Lengkap", text: "Semua isian wajib diisi." });
            return;
        }

        const confirm = await Swal.fire({
            icon: "question",
            title: "Ajukan Lembur?",
            text: "Pastikan data yang diisi sudah benar",
            showCancelButton: true,
            confirmButtonText: "Ya, Ajukan",
            cancelButtonText: "Batal",
        });
        if (!confirm.isConfirmed) return;

        const formData = new FormData();
        formData.append("karyawan_id", karyawanId);
        formData.append("absen_id", "lembur"); // Sesuaikan dengan id lembur di API Anda
        formData.append("periode", getPeriod());
        formData.append("tanggal", tanggalLembur);
        const stringDurasi = `${jamPulangJadwal} s/d ${realisasiPulang} (${formatDurasi(durasiJam)})`;

        const objPayload = {
            nominal: estimasiUang,
            durasi: durasiJam
        };

        formData.append("jam_pulang_jadwal", jamPulangJadwal);
        formData.append("realisasi_pulang", realisasiPulang);
        formData.append("jumlah_jam", durasiJam);
        formData.append("jumlah_hari", durasiJam); // Antisipasi jika API meminjam field jumlah_hari
        formData.append("nominal", estimasiUang); // Mengirim parameter nominal
        formData.append("nama", alasanLembur);

        let namaKoordinator = "";
        try {
            const raw = localStorage.getItem("data_karyawan");
            if (raw) {
                const parsed = JSON.parse(raw);
                namaKoordinator = `${parsed.nama_depan || ""} ${parsed.nama_belakang || ""}`.trim();
            }
        } catch (e) {
            console.error(e);
        }

        // Tambahan komponen terpisah (durasi, payload, koordinator)
        formData.append("durasi", stringDurasi);
        formData.append("payload", JSON.stringify(objPayload));
        formData.append("koordinator", namaKoordinator);

        if (canvasRefPetugas.current) {
            formData.append("ttd_pemohon", canvasRefPetugas.current.toDataURL("image/png"));
        }
        if (canvasRefKoordinator.current) {
            formData.append("ttd_koordinator", canvasRefKoordinator.current.toDataURL("image/png"));
        }

        // Tampilkan success langsung (Background processing)
        Swal.fire({ icon: "success", title: "Berhasil!", text: "Pengajuan lembur sedang diproses...", timer: 1500, showConfirmButton: false });
        onClose?.();

        // Proses API di background
        fetch("https://jne-cilacap.com/slip/public/api/uang", { 
            method: "POST", 
            headers: { "Accept": "application/json" },
            body: formData 
        })
            .then(async (res) => {
                if (!res.ok) {
                    let errMsg = res.statusText;
                    try {
                        const errJson = await res.json();
                        errMsg = errJson.message || errMsg;
                    } catch {
                        const errText = await res.text();
                        if (errText) errMsg = errText.substring(0, 150);
                    }
                    throw new Error(errMsg);
                }
                console.log("Submit lembur sukses (background)");
            })
            .catch(err => {
                console.error("Gagal mengirim lembur (background):", err.message);
                Swal.fire({ icon: "error", title: "Gagal Mengirim", text: "Proses lembur di latar belakang gagal: " + err.message });
            });
    };

    return (
        <div style={{ width: "100%", boxSizing: "border-box" }}>
            <div style={styles.field}>
                <label style={styles.label}>Pilih Karyawan:</label>
                <select ref={tomSelectRef} style={{ width: "100%" }}>
                    <option value="">Pilih karyawan</option>
                </select>
            </div>

            <div style={styles.field}>
                <label style={styles.label}>Hari / Tanggal Lembur:</label>
                <input
                    type="date"
                    value={tanggalLembur}
                    onChange={e => setTanggalLembur(e.target.value)}
                    style={styles.input}
                />
            </div>

            <div style={styles.field}>
                <label style={styles.label}>Jam Pulang (Sesuai Jadwal):</label>
                <input
                    type="time"
                    value={jamPulangJadwal}
                    onChange={e => setJamPulangJadwal(e.target.value)}
                    style={styles.input}
                />
            </div>

            <div style={styles.field}>
                <label style={styles.label}>Realisasi Pulang:</label>
                <input
                    type="time"
                    value={realisasiPulang}
                    onChange={e => setRealisasiPulang(e.target.value)}
                    style={styles.input}
                />
                {(jamPulangJadwal && realisasiPulang) ? (
                    <div style={{ marginTop: 8, fontSize: 13, color: "#2563eb", fontWeight: 500, backgroundColor: "#eff6ff", padding: "10px", borderRadius: "8px", border: "1px dashed #bfdbfe" }}>
                        <div style={{ marginBottom: 4 }}>Total Durasi Lembur: {formatDurasi(durasiJam)}</div>
                        <div>Estimasi Uang Lembur: Rp {estimasiUang.toLocaleString("id-ID")}</div>
                    </div>
                ) : null}
            </div>

            <div style={styles.field}>
                <label style={styles.label}>Alasan / Dalam Rangka Lembur:</label>
                <textarea
                    value={alasanLembur}
                    onChange={e => setAlasanLembur(e.target.value)}
                    placeholder="Contoh: Bongkar muat barang tambahan"
                    rows={3}
                    style={{ ...styles.input, resize: "none" }}
                />
            </div>

            {/* TTD Petugas */}
            <div style={styles.field}>
                <label style={styles.label}>Tanda Tangan Petugas Lembur:</label>
                <canvas
                    ref={canvasRefPetugas}
                    width={300} height={150}
                    style={{ border: "1.5px dashed #ccc", borderRadius: 12, width: "100%", height: 150, backgroundColor: "#f9f9f9", touchAction: "none" }}
                    onMouseDown={(e) => handleDrawStart(isDrawingPetugas, canvasRefPetugas, ctxRefPetugas, e)}
                    onMouseMove={(e) => handleDrawMove(isDrawingPetugas, canvasRefPetugas, ctxRefPetugas, e)}
                    onMouseUp={() => handleDrawEnd(isDrawingPetugas)}
                    onMouseLeave={() => handleDrawEnd(isDrawingPetugas)}
                    onTouchStart={e => { e.preventDefault(); handleDrawStart(isDrawingPetugas, canvasRefPetugas, ctxRefPetugas, e, true); }}
                    onTouchMove={e => { e.preventDefault(); handleDrawMove(isDrawingPetugas, canvasRefPetugas, ctxRefPetugas, e, true); }}
                    onTouchEnd={() => handleDrawEnd(isDrawingPetugas)}
                />
                <button onClick={() => clearSignature(canvasRefPetugas)} style={{ marginTop: 8, color: "#dc2626", background: "none", border: "none", fontSize: 13, cursor: "pointer" }}>Hapus Tanda Tangan</button>
            </div>

            {/* TTD Koordinator */}
            <div style={styles.field}>
                <label style={styles.label}>Tanda Tangan Koordinator / Pengaju:</label>
                <canvas
                    ref={canvasRefKoordinator}
                    width={300} height={150}
                    style={{ border: "1.5px dashed #ccc", borderRadius: 12, width: "100%", height: 150, backgroundColor: "#f9f9f9", touchAction: "none" }}
                    onMouseDown={(e) => handleDrawStart(isDrawingKoordinator, canvasRefKoordinator, ctxRefKoordinator, e)}
                    onMouseMove={(e) => handleDrawMove(isDrawingKoordinator, canvasRefKoordinator, ctxRefKoordinator, e)}
                    onMouseUp={() => handleDrawEnd(isDrawingKoordinator)}
                    onMouseLeave={() => handleDrawEnd(isDrawingKoordinator)}
                    onTouchStart={e => { e.preventDefault(); handleDrawStart(isDrawingKoordinator, canvasRefKoordinator, ctxRefKoordinator, e, true); }}
                    onTouchMove={e => { e.preventDefault(); handleDrawMove(isDrawingKoordinator, canvasRefKoordinator, ctxRefKoordinator, e, true); }}
                    onTouchEnd={() => handleDrawEnd(isDrawingKoordinator)}
                />
                <button onClick={() => clearSignature(canvasRefKoordinator)} style={{ marginTop: 8, color: "#dc2626", background: "none", border: "none", fontSize: 13, cursor: "pointer" }}>Hapus Tanda Tangan</button>
            </div>

            <button
                onClick={handleSubmit}
                style={{ width: "100%", padding: "14px", backgroundColor: "#2563eb", color: "#fff", fontSize: 16, fontWeight: 600, border: "none", borderRadius: 16, cursor: "pointer", marginTop: 4 }}
            >
                Ajukan Lembur
            </button>
        </div>
    );
};

export default LemburForm;
